import { useTranslation } from "react-i18next";
import { IContractDetailsResponseData } from "../../../../setup/api/services/contract/models";
import styles from "./styles.module.scss";
import { useContext, useMemo } from "react";
import PartyTable from "../../create/partyTable";
import { EPARTY_STATUS } from "../../../../enums";
import AmountCard from "../AmountCard";
import { ContractContext } from "../../../../providers/contract";
import { findPartyForProfile } from "../../../../setup/util/contract";

interface CashoutDetailsProps {
  milestone: IContractDetailsResponseData | undefined;
}

const CashoutDetails = ({ milestone }: CashoutDetailsProps) => {
  const { t } = useTranslation();
  const { contract } = useContext(ContractContext);
  const accountId = contract?.organization?.accountId;

  const activeCashOuts = useMemo(
    () =>
      milestone?.cashOutAmountList?.filter((c) => (c.amountDue || 0) > 0) ?? [],
    [milestone?.cashOutAmountList]
  );

  const getLastTransaction = (
    cashOut: { userIdentification?: string; settlementRequestId?: string },
    profileId: number | undefined,
    types: string[]
  ) => {
    if (cashOut.settlementRequestId) {
      const settlementRequest = milestone?.settlementRequests?.find(
        (sr) => sr.id === cashOut.settlementRequestId
      );
      if (settlementRequest?.executionTransactionId) {
        return (
          milestone?.transactions.find(
            (t) =>
              t.id === settlementRequest.executionTransactionId ||
              t.referenceTransactionId ===
                settlementRequest.executionTransactionId
          ) ?? null
        );
      }
      return null;
    }
    const partyTransactions = milestone?.transactions.filter(
      (t) =>
        (t.beneficiaryId === String(profileId) ||
          t.beneficiaryId === cashOut.userIdentification) &&
        types.includes(t.type)
    );
    return partyTransactions?.length
      ? partyTransactions[partyTransactions.length - 1]
      : null;
  };

  const getProfileForCashOut = (userIdentification: string | undefined) => {
    if (!userIdentification) return undefined;
    return (
      milestone?.partiesProfiles?.CUSTOMER?.find(
        (p) => String(p.userIdentification) === userIdentification
      ) ??
      milestone?.partiesProfiles?.PROVIDER?.find(
        (p) => String(p.userIdentification) === userIdentification
      ) ??
      milestone?.partiesProfiles?.BROKER?.find(
        (p) => String(p.userIdentification) === userIdentification
      )
    );
  };

  const getRoleForCashOut = (userIdentification: string | undefined) => {
    if (!userIdentification) return null;
    if (
      milestone?.partiesProfiles?.CUSTOMER?.some(
        (p) => String(p.userIdentification) === userIdentification
      )
    )
      return "CUSTOMER";
    if (
      milestone?.partiesProfiles?.PROVIDER?.some(
        (p) => String(p.userIdentification) === userIdentification
      )
    )
      return "PROVIDER";
    if (
      milestone?.partiesProfiles?.BROKER?.some(
        (p) => String(p.userIdentification) === userIdentification
      )
    )
      return "BROKER";
    return null;
  };

  const customers = useMemo(
    () =>
      activeCashOuts.filter(
        (c) => getRoleForCashOut(c.userIdentification) === "CUSTOMER"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCashOuts, milestone?.partiesProfiles]
  );

  const providers = useMemo(
    () =>
      activeCashOuts.filter(
        (c) => getRoleForCashOut(c.userIdentification) === "PROVIDER"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCashOuts, milestone?.partiesProfiles]
  );

  const brokers = useMemo(
    () =>
      activeCashOuts.filter(
        (c) => getRoleForCashOut(c.userIdentification) === "BROKER"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCashOuts, milestone?.partiesProfiles]
  );

  const amountSettled = useMemo(() => {
    const admin = milestone?.cashOutAmountList?.find(
      (amount) => amount?.id === accountId
    );
    return admin?.amountDue !== undefined
      ? admin?.amountDue + (milestone?.fee || 0)
      : 0;
  }, [milestone?.cashOutAmountList, milestone?.fee, accountId]);

  const profit = useMemo(() => {
    const admin = milestone?.cashOutAmountList?.find(
      (amount) => amount?.id === accountId
    );
    return admin?.amountDue !== undefined ? admin?.amountDue : 0;
  }, [milestone?.cashOutAmountList, accountId]);

  return (
    <>
      {!!customers?.length && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_payees")}</h2>

          <PartyTable
            detailsPage
            rows={customers.map((cashOut) => {
              const profile = getProfileForCashOut(cashOut.userIdentification);
              const currentParty = findPartyForProfile(
                milestone?.parties,
                profile
              );
              const partyLastTransaction = getLastTransaction(
                cashOut,
                profile?.id,
                ["CASH_OUT", "REFUND_CUSTOMER"]
              );
              return {
                id: profile?.id?.toString() || "",
                name: profile?.profile?.fullName || "",
                phoneNumber:
                  profile?.phoneNumber || currentParty?.phoneNumber || "",
                amount: cashOut?.amountDue || 0,
                status:
                  currentParty?.status === EPARTY_STATUS.REFUNDED ||
                  currentParty?.status === EPARTY_STATUS.RELEASED
                    ? currentParty.status
                    : cashOut.settlementAmountStatus === "TRANSFERRED"
                    ? EPARTY_STATUS.RELEASED
                    : cashOut.settlementAmountStatus ===
                      String(EPARTY_STATUS.AVAILABLE_BALANCE)
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : EPARTY_STATUS.WAITING_FOR_RELEASE,
                type: "customer",
                fee: 0,
                total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
                image: profile?.profile?.photoUrl || "",
                cashoutTime: partyLastTransaction
                  ? partyLastTransaction.createdAt
                  : undefined,
                referenceNumber: partyLastTransaction
                  ? partyLastTransaction.id
                  : undefined,
              };
            })}
            columns={{
              showActions: false,
              showStatus: true,
              showRole: false,
              showFee: false,
              showTotal: false,
              showCashoutTime: true,
              showReferenceNumber: true,
            }}
            getIndexByRow={(row) =>
              (milestone?.parties || [])?.findIndex(
                (p) => p?.id?.toString() === row?.id
              )
            }
          />
        </div>
      )}

      {!!providers?.length && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_receivers")}</h2>

          <PartyTable
            detailsPage
            rows={providers.map((cashOut) => {
              const profile = getProfileForCashOut(cashOut.userIdentification);
              const currentParty = findPartyForProfile(
                milestone?.parties,
                profile
              );
              const partyLastTransaction = getLastTransaction(
                cashOut,
                profile?.id,
                ["CASH_OUT", "RELEASE_TO_PROVIDER", "CLOSE_RELEASE_TO_PROVIDER"]
              );
              return {
                id: profile?.id?.toString() || "",
                name: profile?.profile?.fullName || "",
                phoneNumber:
                  profile?.phoneNumber || currentParty?.phoneNumber || "",
                amount: cashOut?.amountDue || 0,
                status:
                  currentParty?.status === EPARTY_STATUS.RELEASED
                    ? currentParty.status
                    : cashOut.settlementAmountStatus ===
                      String(EPARTY_STATUS.AVAILABLE_BALANCE)
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : EPARTY_STATUS.WAITING_FOR_RELEASE,
                type: "provider",
                fee: 0,
                total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
                image: profile?.profile?.photoUrl || "",
                cashoutTime: partyLastTransaction
                  ? partyLastTransaction.createdAt
                  : undefined,
                referenceNumber: partyLastTransaction
                  ? partyLastTransaction.id
                  : undefined,
              };
            })}
            columns={{
              showActions: false,
              showStatus: true,
              showRole: false,
              showFee: false,
              showTotal: false,
              showCashoutTime: true,
              showReferenceNumber: true,
            }}
            getIndexByRow={(row) =>
              (milestone?.parties || [])?.findIndex(
                (p) => p?.id?.toString() === row?.id
              )
            }
          />
        </div>
      )}

      {!!brokers?.length && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_brokers")}</h2>

          <PartyTable
            detailsPage
            rows={brokers.map((cashOut) => {
              const profile = getProfileForCashOut(cashOut.userIdentification);
              const currentParty = findPartyForProfile(
                milestone?.parties,
                profile
              );
              const partyLastTransaction = getLastTransaction(
                cashOut,
                profile?.id,
                ["CASH_OUT", "RELEASE_TO_PROVIDER", "CLOSE_RELEASE_TO_PROVIDER"]
              );
              return {
                id: profile?.id?.toString() || "",
                name: profile?.profile?.fullName || "",
                phoneNumber:
                  profile?.phoneNumber || currentParty?.phoneNumber || "",
                amount: cashOut?.amountDue || 0,
                status:
                  currentParty?.status === EPARTY_STATUS.RELEASED
                    ? currentParty.status
                    : cashOut.settlementAmountStatus ===
                      String(EPARTY_STATUS.AVAILABLE_BALANCE)
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : EPARTY_STATUS.WAITING_FOR_RELEASE,
                type: "provider",
                fee: 0,
                total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
                image: profile?.profile?.photoUrl || "",
                cashoutTime: partyLastTransaction
                  ? partyLastTransaction.createdAt
                  : undefined,
                referenceNumber: partyLastTransaction
                  ? partyLastTransaction.id
                  : undefined,
              };
            })}
            columns={{
              showActions: false,
              showStatus: true,
              showRole: false,
              showFee: false,
              showTotal: false,
              showCashoutTime: true,
              showReferenceNumber: true,
            }}
            getIndexByRow={(row) =>
              (milestone?.parties || [])?.findIndex(
                (p) => p?.id?.toString() === row?.id
              )
            }
          />
        </div>
      )}

      <div className={styles.cardsContainer}>
        <AmountCard
          title={t("lbl_value_assigned_to_you")}
          amount={String(amountSettled?.toFixed(2))}
          style={{ backgroundColor: "#F8F9FB" }}
          isSmall
        />
        <AmountCard
          title={t("lbl_waffy_fees_amount")}
          amount={String((milestone?.fee || 0)?.toFixed(2))}
          style={{ backgroundColor: "#F8F9FB" }}
          isSmall
        />
        <AmountCard
          title={t("lbl_your_profit")}
          amount={String(profit?.toFixed(2))}
          style={{
            backgroundColor: profit === 0 ? "#F8F9FB" : undefined,
          }}
          specialStyle={
            profit > 0 ? "positive" : profit < 0 ? "negative" : undefined
          }
          isSmall
        />
      </div>
    </>
  );
};

export default CashoutDetails;
